import React, { useEffect, useState } from 'react';
import { cn } from '../../../../lib/utils';
import { reportsApi } from '../../api/reports';
import TableJoinModal from './TableJoinModal';
import {
  emptyReportQuery,
  normalizeReportQueries,
} from '../../utils/reportQueries';

const inputCls = 'w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200';

export default function ReportQueriesEditor({ queries, onChange }) {
  const list = normalizeReportQueries(queries);
  const [sqlTables, setSqlTables] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    reportsApi.tables()
      .then((res) => setSqlTables(res.data?.tables || res.data?.allowed || []))
      .catch(() => setSqlTables([]));
  }, []);

  useEffect(() => {
    const missing = list.filter((q) => q.sqlQuery && !(q.fields || []).length);
    if (!missing.length) return undefined;
    let cancelled = false;
    Promise.all(missing.map((q) =>
      reportsApi.discoverSql(q.sqlQuery)
        .then((res) => ({ id: q.id, data: res.data }))
        .catch(() => null),
    )).then((results) => {
      if (cancelled) return;
      let changed = false;
      const next = list.map((q) => {
        const found = results.find((r) => r && r.id === q.id);
        if (!found?.data) return q;
        const fields = (found.data.fields || []).map((f) => (
          typeof f === 'string' ? { key: f, fa: f } : { key: f.key, fa: f.fa || f.label || f.key }
        )).filter((f) => f.key);
        if (!fields.length) return q;
        changed = true;
        return { ...q, fields };
      });
      if (changed) onChange(next);
    });
    return () => { cancelled = true; };
    // Discover only when a query has SQL but no stored fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.map((q) => `${q.id}:${q.sqlQuery}:${(q.fields || []).length}`).join('|')]);

  const editing = list.find((q) => q.id === editingId) || null;

  const setList = (next) => onChange(next);

  const addQuery = () => {
    const q = emptyReportQuery(list);
    setList([...list, q]);
    setEditingId(q.id);
  };

  const patchQuery = (id, patch) => {
    setList(list.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const removeQuery = (id) => {
    setList(list.filter((q) => q.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const handleJoinApply = ({ sql, joinConfig, fields, dimensions, metrics }) => {
    if (!editingId) return;
    const mapped = (fields || []).map((f) => (
      typeof f === 'string' ? { key: f, fa: f } : { key: f.key, fa: f.fa || f.label || f.key }
    ));
    const fromDims = (dimensions || []).map((k) => ({ key: k, fa: k }));
    const fromMets = (metrics || []).map((k) => ({ key: k, fa: k }));
    const merged = [...mapped, ...fromDims, ...fromMets].filter((f, i, a) => f.key && a.findIndex((x) => x.key === f.key) === i);
    patchQuery(editingId, {
      sqlQuery: sql,
      joinConfig: joinConfig || null,
      fields: merged,
    });
    setEditingId(null);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50 rtl">
      <div className="shrink-0 border-b border-slate-100 px-3 py-3">
        <p className="text-sm font-bold text-slate-800">کوئری‌های گزارش</p>
        <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
          این کوئری‌ها یک‌بار هنگام باز شدن صفحه اجرا می‌شوند. کارت‌های KPI و گیج فیلدهایشان را از همین نتایج پر می‌کنند — لازم نیست برای هر کارت یک کوئری جدا اجرا شود.
        </p>
        <button
          type="button"
          onClick={addQuery}
          className="mt-2 w-full rounded-lg bg-indigo-600 py-2 text-xs font-bold text-white shadow-sm hover:bg-indigo-700"
        >
          + افزودن کوئری
        </button>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {list.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-8 text-center text-[11px] leading-relaxed text-slate-400">
            هنوز کوئری‌ای نیست. یکی بسازید و فیلدهای فروش، هدف و … را از آن استخراج کنید.
          </div>
        )}
        {list.map((q, index) => (
          <div key={q.id} className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 px-2.5 py-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-[10px] font-bold text-indigo-700">
                {index + 1}
              </span>
              <input
                className={cn(inputCls, 'flex-1')}
                value={q.name || ''}
                onChange={(e) => patchQuery(q.id, { name: e.target.value })}
                placeholder={`کوئری ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => removeQuery(q.id)}
                className="shrink-0 text-[10px] text-rose-500"
              >
                حذف
              </button>
            </div>
            <div className="space-y-2 border-t border-slate-100 px-2.5 py-2">
              <button
                type="button"
                onClick={() => setEditingId(q.id)}
                className="w-full rounded-lg border border-indigo-200 bg-indigo-50 py-2 text-[11px] font-bold text-indigo-700 hover:bg-indigo-100"
              >
                {q.sqlQuery ? 'ویرایش کوئری' : '⊕ ساخت کوئری'}
              </button>
              {q.sqlQuery ? (
                <pre className="max-h-28 overflow-auto rounded-md bg-slate-900 p-2 text-left text-[9px] text-emerald-300 dir-ltr whitespace-pre-wrap">
                  {q.sqlQuery}
                </pre>
              ) : (
                <p className="text-[10px] text-slate-400">SQL هنوز تنظیم نشده است.</p>
              )}
              {(q.fields || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {q.fields.map((f) => (
                    <span
                      key={f.key || f}
                      className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] text-slate-600"
                    >
                      {f.fa || f.label || f.key || f}
                    </span>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 text-[11px] text-slate-500">
                حداکثر ردیف
                <input
                  type="number"
                  min={1}
                  max={10000}
                  className={cn(inputCls, 'w-20')}
                  value={q.limit || 500}
                  onChange={(e) => patchQuery(q.id, { limit: Number(e.target.value) })}
                />
              </label>
            </div>
          </div>
        ))}
      </div>

      <TableJoinModal
        key={editing?.id || 'none'}
        open={!!editing}
        onClose={() => setEditingId(null)}
        tablesCatalog={sqlTables}
        initialConfig={editing?.joinConfig || null}
        initialSql={editing?.sqlQuery || ''}
        tableColumns={[]}
        onApply={handleJoinApply}
      />
    </div>
  );
}
